/*
  Load more / infinite scroll for collection and search grids.
  2026-10-13 (Fix List F14):
  - Each "Show more" now fetches only the product-grid section (section_id=...) instead of the whole page.
  - What was loaded, plus the scroll position, is remembered for 30 minutes, so coming Back from a product
    page restores the grid where the shopper left it (browsers without back-forward cache used to drop
    everything but the first page).
  2026-10-14: the saved grid is restored only on Back/Forward. A menu tap, typed URL or reload is a fresh
    visit and opens the collection at the top (it used to land mid-grid on the card from last time).
*/
class InfiniteScroll extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', this.onClickHandler.bind(this));

    if (this.dataset.trigger == 'infinite') {
      new IntersectionObserver(this.handleIntersection.bind(this), {rootMargin: '0px 0px 1200px 0px'}  /* 2026-10-14: a fast flick used to skip the 200px trigger */).observe(this);
    }
  }

  onClickHandler() {
    if (this.classList.contains('loading') || this.classList.contains('disabled')) return;
    this.classList.add('loading');
    this.classList.add('disabled');

    const sections = InfiniteScroll.getSections();
    sections.forEach((section) => {
      const url = InfiniteScroll.sectionUrl(this.dataset.url, section.section);
      InfiniteScroll.renderSectionFromFetch(url);
    });
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    this.onClickHandler();
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  // Ask Shopify for just this section's HTML, not the whole page.
  static sectionUrl(url, sectionId) {
    try {
      const u = new URL(url, window.location.origin);
      if (sectionId) u.searchParams.set('section_id', sectionId);
      return u.pathname + u.search;
    } catch (e) {
      return url;
    }
  }

  static renderSectionFromFetch(url) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        InfiniteScroll.renderPagination(html);
        InfiniteScroll.renderProductGridContainer(html);
        if (typeof getCheckedColors === 'function') getCheckedColors();
        InfiniteScroll.saveState();
      })
      .catch((e) => {
        console.error(e);
      });
  }

  static renderPagination(html) {
    const container = document.getElementById('ProductGridContainer').querySelector('.pagination-wrapper');
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const parsedContainer = parsed.getElementById('ProductGridContainer') || parsed.body;
    const pagination = parsedContainer.querySelector('.pagination-wrapper');
    if (!container) return;
    if (pagination) {
      container.innerHTML = pagination.innerHTML;
    }
    else {
      container.remove();
    }
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById('product-grid');
    const products = new DOMParser().parseFromString(html, 'text/html').getElementById('product-grid');
    if (!container || !products) return;
    container.insertAdjacentHTML('beforeend', products.innerHTML);
  }

  // --- remember the loaded grid so Back brings the shopper to the same spot ---
  static stateKey() {
    return 'si-grid:' + window.location.pathname + window.location.search;
  }

  static saveState() {
    try {
      const grid = document.getElementById('product-grid');
      if (!grid) return;
      const wrapper = document.querySelector('#ProductGridContainer .pagination-wrapper');
      const next = wrapper ? wrapper.querySelector('infinite-scroll') : null;
      // Remember which product card was at the top of the screen: images load later and shift the page,
      // so a card is a steadier anchor than a pixel position.
      let anchorHref = null;
      for (const link of grid.querySelectorAll('li a[href*="/products/"]')) {
        if (link.getBoundingClientRect().bottom > 0) { anchorHref = link.getAttribute('href'); break; }
      }
      sessionStorage.setItem(InfiniteScroll.stateKey(), JSON.stringify({
        html: grid.innerHTML,
        pagination: wrapper ? wrapper.innerHTML : null,
        nextUrl: next ? next.dataset.url : null,
        scrollY: window.scrollY,
        anchorHref: anchorHref,
        t: Date.now()
      }));
    } catch (e) { /* storage unavailable: nothing to do */ }
  }

  // True only when the shopper arrived with the browser's Back or Forward button.
  static isHistoryNavigation() {
    try {
      const entry = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      if (entry && entry.type) return entry.type === 'back_forward';
      return !!(performance.navigation && performance.navigation.type === 2);
    } catch (e) { return false; }
  }

  static restoreState() {
    try {
      // Only Back/Forward brings the grid back. A menu tap, typed URL or reload is a fresh visit:
      // throw the saved grid away so the collection opens at the top.
      if (!InfiniteScroll.isHistoryNavigation()) { sessionStorage.removeItem(InfiniteScroll.stateKey()); return; }
      const raw = sessionStorage.getItem(InfiniteScroll.stateKey());
      if (!raw) return;
      const state = JSON.parse(raw);
      if (!state || Date.now() - state.t > 30 * 60 * 1000) { sessionStorage.removeItem(InfiniteScroll.stateKey()); return; }
      const grid = document.getElementById('product-grid');
      if (!grid || grid.children.length >= (state.html.match(/<li\b/g) || []).length) return; // already showing as much
      grid.innerHTML = state.html;
      const wrapper = document.querySelector('#ProductGridContainer .pagination-wrapper');
      if (wrapper) {
        if (state.pagination) wrapper.innerHTML = state.pagination; else wrapper.remove();
      }
      if (typeof getCheckedColors === 'function') getCheckedColors();
      const y = state.scrollY || 0;
      const scrollBack = () => {
        const anchor = state.anchorHref ? grid.querySelector('li a[href="' + state.anchorHref.replace(/"/g, '') + '"]') : null;
        if (anchor) anchor.closest('li').scrollIntoView({ block: 'start' }); else window.scrollTo(0, y);
      };
      requestAnimationFrame(scrollBack); setTimeout(scrollBack, 300); setTimeout(scrollBack, 900);
    } catch (e) { /* ignore */ }
  }
}
customElements.define('infinite-scroll', InfiniteScroll);

// Keep the scroll position current while the shopper browses, and restore on return.
window.addEventListener('pagehide', () => {
  if (document.getElementById('product-grid') && sessionStorage.getItem(InfiniteScroll.stateKey())) InfiniteScroll.saveState();
});
// Restore straight away (this script is deferred, so the grid is already on the page) rather than on
// 'pageshow': by then the first page's "show more" trigger could already be fetching, and that fetch
// would land on top of the restored grid as a duplicate page. A bfcache return never re-runs this script.
InfiniteScroll.restoreState();
